/**
 * Applies stamp voxels to chunk data
 * Handles placement at terrain height and blending with existing terrain
 */
import { CHUNK_SIZE, VOXEL_SCALE } from '../../voxel/constants.js';
import { hashInt2 } from '../../util/rng.js';
import { packVoxel, getWeight, setWeight, voxelIndex } from '../../voxel/voxelData.js';
import { getStamp, isBuildingStamp } from './registry.js';
export var BlendMode;
(function (BlendMode) {
    /** New voxel replaces old if more solid */
    BlendMode["MAX_WEIGHT"] = "max_weight";
    /** New voxel always wins if it's solid */
    BlendMode["REPLACE_SOLID"] = "replace_solid";
    /** Additive blending (combine weights) */
    BlendMode["ADDITIVE"] = "additive";
})(BlendMode || (BlendMode = {}));
/**
 * The stamp-local row a model's y=0 lands on, given the terrain height under it (fine voxels).
 *
 * ROUND FOR BUILDINGS ABOVE LOD 0, FLOOR FOR EVERYTHING ELSE, and both halves are measured.
 *
 * Flooring puts a model on the coarse row at or BELOW the surface while the drawn terrain crosses at
 * terrainHeight itself, so the stamp is buried by 0 to one full coarse voxel — half a voxel on average,
 * and always downward. For a building that is the reported "the hut looks shifted down about a voxel":
 * 0.5 m at lod 1, 2 m at lod 3, against a wall line you can see. Rounding centres the error at ±half a
 * voxel, and it cannot leave a building hanging, because every building model reaches at least one
 * coarse voxel below its origin (LOD 0's own foundation in a fold, the plinth in a stand-in).
 *
 * A TREE KEEPS FLOORING, and not for want of tidiness. Half a voxel of seat error is invisible inside a
 * canopy, and moving foliage UP is not free: coarse leaves are a TRANSPARENT material, so every leaf
 * voxel is a hole in the solid mesh layer, and a hole that lands in the shared cell column at a chunk
 * plane is a band the clip has promised is solid. Measured with trees rounded: 25.0 m of open same-LOD
 * solid seam at lod 5 (client/src/game/voxel/coarseSeamOwnership.test.ts), gone when only buildings
 * round. LOD 0 floors for everything — the stampCache checksum is that arithmetic.
 *
 * EXPORTED because the coarse column clip has to ask the same question (TerrainGenerator's stamp clip
 * span): it decides whether the chunk a stamp reaches into is loaded at all, and a clip working from a
 * different seat than the placer is a band nobody draws.
 */
export function stampSeatRow(terrainHeight, lod, yOffset = 0, centred = false) {
    const stride = 1 << lod;
    const seat = (lod > 0 && centred)
        ? Math.round(terrainHeight / stride)
        : Math.floor(terrainHeight / stride);
    return seat + Math.round(yOffset / stride);
}
/**
 * The per-instance seed of a stamp at a world position, in metres — decorations, and the tower's
 * floor count. Shared by the placer (which draws the model) and the coarse column clip (which asks
 * how tall that model came out), because a second copy of this expression would let the two disagree
 * about which tower they are talking about.
 */
export function stampInstanceSeed(worldX, worldZ) {
    return hashInt2(Math.floor(worldX / VOXEL_SCALE), Math.floor(worldZ / VOXEL_SCALE));
}
/** Bits of a stamp instance's mirroring: FLIP_X mirrors left-right (x -> -x), FLIP_Z front-back. */
export const FLIP_X = 1;
export const FLIP_Z = 2;
/**
 * THE MIRRORING OF A STAMP INSTANCE — 0..3 as FLIP_X | FLIP_Z — from its world position.
 *
 * Four orientations of every cached model for free: the placer negates a voxel's local x and/or z as
 * it READS the one cached list, so a stand of one shape stops reading as clones and the cache holds
 * nothing extra. Taken from the instance seed's upper bits rather than from the scatter's RNG, so
 * positions, variants, yaws and decorations are exactly what they were; and from the POSITION rather
 * than any per-chunk state, so every chunk, every LOD, the placer and the column clip see the same
 * flip — everything the placer places is a voxel list, so one transform covers every ring, and a
 * tree cannot mirror itself between them. Bits 8-9: the low bits seed the decoration RNG.
 */
export function stampFlip(worldX, worldZ) {
    return (stampInstanceSeed(worldX, worldZ) >>> 8) & 3;
}
/** A stamp's bounds as its mirrored instance occupies them. */
export function flippedBounds(b, flip) {
    return {
        minX: flip & FLIP_X ? -b.maxX : b.minX, maxX: flip & FLIP_X ? -b.minX : b.maxX,
        minY: b.minY, maxY: b.maxY,
        minZ: flip & FLIP_Z ? -b.maxZ : b.minZ, maxZ: flip & FLIP_Z ? -b.minZ : b.maxZ,
    };
}
/**
 * Voxels of a cached stamp sorted ascending by local Y (built once, memoised on the stamp) as flat
 * typed arrays. Lets a caller iterate only the Y-slice of a tall stamp that intersects a given chunk
 * instead of scanning every voxel once per chunk it spans. THE SCALED PATH'S INDEX; the unscaled path
 * uses `stampBuckets`, which adds the XZ dimension. Two stamp voxels can only land on the same
 * chunk cell when they share the same local (x,y,z) — hence the same Y — and the sort is stable (original
 * index tiebreak), so voxels that collide keep their original blend order: output is byte-identical.
 */
function stampVoxelsByY(stamp) {
    const holder = stamp;
    let s = holder._byY;
    if (!s) {
        const v = stamp.voxels;
        const n = v.length;
        const idx = v.map((_, i) => i);
        idx.sort((a, b) => (v[a].y - v[b].y) || (a - b));
        const xs = new Int32Array(n), ys = new Int32Array(n), zs = new Int32Array(n), mats = new Int32Array(n);
        const weights = new Float64Array(n);
        for (let k = 0; k < n; k++) {
            const vv = v[idx[k]];
            xs[k] = vv.x;
            ys[k] = vv.y;
            zs[k] = vv.z;
            mats[k] = vv.material;
            weights[k] = vv.weight;
        }
        s = { n, xs, ys, zs, mats, weights };
        holder._byY = s;
    }
    return s;
}
/** Width of an XZ bucket, as a shift: 8 of the stamp's own voxels. A quarter of a chunk: fine enough
 *  that a chunk overlaps 3-4 bands per axis, coarse enough that a small tree is a few buckets. */
const BUCKET_SHIFT = 3;
function stampBuckets(stamp) {
    const holder = stamp;
    let b = holder._buckets;
    if (b)
        return b;
    const v = stamp.voxels;
    const n = v.length;
    const bounds = stamp.bounds;
    const bx0 = bounds.minX >> BUCKET_SHIFT, bz0 = bounds.minZ >> BUCKET_SHIFT;
    const nbx = (bounds.maxX >> BUCKET_SHIFT) - bx0 + 1;
    const nbz = (bounds.maxZ >> BUCKET_SHIFT) - bz0 + 1;
    const bucketOf = (i) => ((v[i].z >> BUCKET_SHIFT) - bz0) * nbx + ((v[i].x >> BUCKET_SHIFT) - bx0);
    const idx = v.map((_, i) => i);
    // (bucket, y, original index): the last key is what keeps same-position entries in their
    // original order, which is the byte-identity argument above.
    idx.sort((a, c) => (bucketOf(a) - bucketOf(c)) || (v[a].y - v[c].y) || (a - c));
    const xs = new Int32Array(n), ys = new Int32Array(n), zs = new Int32Array(n), mats = new Int32Array(n);
    const weights = new Float64Array(n);
    const start = new Int32Array(nbx * nbz + 1);
    for (let k = 0; k < n; k++) {
        const vv = v[idx[k]];
        xs[k] = vv.x;
        ys[k] = vv.y;
        zs[k] = vv.z;
        mats[k] = vv.material;
        weights[k] = vv.weight;
        start[bucketOf(idx[k]) + 1]++;
    }
    for (let i = 1; i < start.length; i++)
        start[i] += start[i - 1];
    b = { xs, ys, zs, mats, weights, bx0, bz0, nbx, nbz, start };
    holder._buckets = b;
    return b;
}
/**
 * The fine-voxel box a stamp occupies, or null if it draws nothing at this level.
 *
 * ONE DEFINITION, because three things ask it and they must not disagree: the node walk prunes with
 * it, the coarse column clip decides a chunk EXISTS from it, and the placer writes inside it. A clip
 * working from different arithmetic than the draw is a band nobody meshes — which is why the seat
 * comes from `stampSeatRow` here rather than being re-derived.
 */
export function stampFineBounds(placement, lod, heightSampler, scale = 1) {
    const stride = 1 << lod;
    const seed = stampInstanceSeed(placement.worldX, placement.worldZ);
    const stamp = getStamp(placement.type, placement.variant, placement.rotation, seed, lod);
    if (!stamp)
        return null;
    const originX = Math.floor(placement.worldX / (VOXEL_SCALE * stride));
    const originZ = Math.floor(placement.worldZ / (VOXEL_SCALE * stride));
    const terrainHeight = lod === 0
        ? heightSampler.sampleHeight(placement.worldX, placement.worldZ)
        : heightSampler.sampleHeight(originX * stride * VOXEL_SCALE, originZ * stride * VOXEL_SCALE);
    const seat = stampSeatRow(terrainHeight, lod, placement.yOffset ?? 0, isBuildingStamp(placement.type));
    const b = flippedBounds(stamp.bounds, stampFlip(placement.worldX, placement.worldZ));
    // The scaled placement path rounds each voxel offset to the lattice, so the box grows with it.
    // Rounded OUTWARD: this box may be too big without consequence, and must never be too small.
    const g = (v) => (scale === 1 ? v : (v < 0 ? Math.floor(v * scale) : Math.ceil(v * scale)));
    return {
        minX: (originX + g(b.minX)) * stride,
        maxX: (originX + g(b.maxX) + 1) * stride,
        minY: (seat + g(b.minY)) * stride,
        maxY: (seat + g(b.maxY) + 1) * stride,
        minZ: (originZ + g(b.minZ)) * stride,
        maxZ: (originZ + g(b.maxZ) + 1) * stride,
    };
}
// ============== Stamp Placer ==============
const DEFAULT_CONFIG = {
    blendMode: BlendMode.REPLACE_SOLID,
};
/**
 * Apply stamps to chunk voxel data
 */
export class StampPlacer {
    config;
    constructor(config = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
    }
    /**
     * Apply multiple stamp placements to chunk data
     * Modifies the data array in place
     *
     * @param data - Chunk voxel data (CHUNK_SIZE^3 Uint32Array)
     * @param cx - Chunk X coordinate
     * @param cy - Chunk Y coordinate
     * @param cz - Chunk Z coordinate
     * @param placements - Stamp placements to apply
     * @param heightSampler - Function to get terrain height
     */
    applyStamps(data, cx, cy, cz, placements, heightSampler, scale = 1, lod = 0) {
        // Chunk bounds in voxel space
        const chunkVoxelX = cx * CHUNK_SIZE;
        const chunkVoxelY = cy * CHUNK_SIZE;
        const chunkVoxelZ = cz * CHUNK_SIZE;
        // Sort placements so buildings are applied last
        // This ensures their interior carving voxels remove any overlapping trees/rocks
        const sortedPlacements = [...placements].sort((a, b) => {
            const aIsBuilding = isBuildingStamp(a.type);
            const bIsBuilding = isBuildingStamp(b.type);
            if (aIsBuilding && !bIsBuilding)
                return 1; // a after b
            if (!aIsBuilding && bIsBuilding)
                return -1; // a before b
            return 0;
        });
        for (const placement of sortedPlacements) {
            this.applyStamp(data, placement, chunkVoxelX, chunkVoxelY, chunkVoxelZ, heightSampler, scale, lod);
        }
    }
    /**
     * ONE stamp into one chunk — the per-node entry point (terrain/nodes/kinds/scatter.ts).
     *
     * `applyStamps` above is the list form and stays: it owns the BUILDINGS-LAST ordering, which is a
     * property of a list and not of any stamp in it. A caller that supplies its own order (the node
     * walk does — children are returned already sorted) comes in here instead.
     */
    applyOne(data, cx, cy, cz, placement, heightSampler, scale = 1, lod = 0) {
        this.applyStamp(data, placement, cx * CHUNK_SIZE, cy * CHUNK_SIZE, cz * CHUNK_SIZE, heightSampler, scale, lod);
    }
    /**
     * Apply a single stamp to chunk data
     */
    applyStamp(data, placement, chunkVoxelX, chunkVoxelY, chunkVoxelZ, heightSampler, scale = 1, lod = 0) {
        // Deterministic per-building seed from world position (stable across chunks) → decorations, and
        // the tower's floor count. Shared with the coarse column clip (see stampInstanceSeed).
        const seed = stampInstanceSeed(placement.worldX, placement.worldZ);
        // Pass rotation + seed to getStamp for buildings (SDF-based rotation)
        const stamp = getStamp(placement.type, placement.variant, placement.rotation, seed, lod);
        if (!stamp)
            return; // this type has no model at this LOD
        // Stamp origin in voxel coordinates (yOffset seats e.g. a torch on top of a wall).
        // A coarse stamp's voxels are on the COARSE lattice, so its origin is too: one divide per axis is
        // the ONLY difference between placing a stand-in and placing the real thing. lodStride is 1 at
        // lod 0, so the LOD-0 arithmetic is untouched (the stampCache checksum depends on that).
        const lodStride = 1 << lod;
        const stampOriginX = Math.floor(placement.worldX / (VOXEL_SCALE * lodStride));
        const stampOriginZ = Math.floor(placement.worldZ / (VOXEL_SCALE * lodStride));
        // SEAT A COARSE STAMP ON THE HEIGHT ITS COLUMN USES, NOT THE HEIGHT UNDER THE TREE.
        //
        // generateChunkLOD fills a coarse column from the height sampled at that column's LATTICE POINT.
        // Sampling instead at the stamp's exact position disagrees with it by however much the terrain
        // moves across one coarse voxel — 8 m of ground at lod 5 — so on a slope the stamp is seated tens
        // of metres off. Measured at lod 5: 686 of 1451 leaf voxels ended up BELOW the surface, the
        // deepest by 179 fine voxels (45 m). Buried foliage is not merely invisible: leaves are a
        // TRANSPARENT material, so each buried voxel is a hole in the solid mesh layer, i.e. a meshable
        // surface underground in a chunk the column clip has promised is solid rock — which is exactly
        // the orphaned band lodColumnSeam reports. Snapping the sample to the lattice makes the stamp and
        // its column agree by construction. LOD 0 keeps the exact position (stride 1, no snapping).
        const terrainHeight = lod === 0
            ? heightSampler.sampleHeight(placement.worldX, placement.worldZ)
            : heightSampler.sampleHeight(stampOriginX * lodStride * VOXEL_SCALE, stampOriginZ * lodStride * VOXEL_SCALE);
        const stampOriginY = stampSeatRow(terrainHeight, lod, placement.yOffset ?? 0, isBuildingStamp(placement.type));
        // Only this chunk's slice of the stamp can write here — a tall stamp spans several chunks and a
        // wide one several columns, so iterating its full voxel list per chunk re-scans everything that
        // misses. The voxels are indexed by XZ bucket and Y-sorted within each (cached on the stamp).
        const loY = chunkVoxelY - stampOriginY; // inclusive local-Y of this chunk's bottom
        const baseX = stampOriginX - chunkVoxelX, baseZ = stampOriginZ - chunkVoxelZ;
        // THE MIRROR (see stampFlip): a voxel at stamp-local x lands at base + sx·x.
        const flip = stampFlip(placement.worldX, placement.worldZ);
        const sx = flip & FLIP_X ? -1 : 1, sz = flip & FLIP_Z ? -1 : 1;
        if (scale === 1) {
            // Fast path (default): the buckets this chunk overlaps, each binary-searched to its [loY, hiY)
            // Y-window. Byte-identical to the single Y-sorted walk it replaced (see stampBuckets) — the
            // stampCache guard depends on this staying exact.
            const bk = stampBuckets(stamp);
            const ys = bk.ys, xs = bk.xs, zs = bk.zs, mats = bk.mats, weights = bk.weights, start = bk.start;
            // Stamp-local X/Z range covered by this chunk: local = base + s·stampLocal ∈ [0, CHUNK_SIZE),
            // so stampLocal ∈ [-base, CHUNK-1-base] unmirrored and [base-(CHUNK-1), base] mirrored.
            const xLo = sx > 0 ? -baseX : baseX - (CHUNK_SIZE - 1), xHi = sx > 0 ? CHUNK_SIZE - 1 - baseX : baseX;
            const zLo = sz > 0 ? -baseZ : baseZ - (CHUNK_SIZE - 1), zHi = sz > 0 ? CHUNK_SIZE - 1 - baseZ : baseZ;
            const bxLo = Math.max(bk.bx0, xLo >> BUCKET_SHIFT);
            const bxHi = Math.min(bk.bx0 + bk.nbx - 1, xHi >> BUCKET_SHIFT);
            const bzLo = Math.max(bk.bz0, zLo >> BUCKET_SHIFT);
            const bzHi = Math.min(bk.bz0 + bk.nbz - 1, zHi >> BUCKET_SHIFT);
            for (let bz = bzLo; bz <= bzHi; bz++) {
                for (let bx = bxLo; bx <= bxHi; bx++) {
                    const bucket = (bz - bk.bz0) * bk.nbx + (bx - bk.bx0);
                    let a = start[bucket];
                    const end = start[bucket + 1];
                    let b = end;
                    while (a < b) {
                        const m = (a + b) >> 1;
                        if (ys[m] < loY)
                            a = m + 1;
                        else
                            b = m;
                    }
                    for (let i = a; i < end; i++) {
                        const localY = ys[i] - loY; // = stampOriginY + voxel.y - chunkVoxelY
                        if (localY >= CHUNK_SIZE)
                            break; // past this chunk's Y-window (sorted → done)
                        const localX = baseX + sx * xs[i];
                        const localZ = baseZ + sz * zs[i];
                        if (localX < 0 || localX >= CHUNK_SIZE || localZ < 0 || localZ >= CHUNK_SIZE)
                            continue;
                        const index = voxelIndex(localX, localY, localZ);
                        const newVoxel = this.blendVoxel(data[index], mats[i], weights[i]);
                        if (newVoxel !== null)
                            data[index] = newVoxel;
                    }
                }
            }
            return;
        }
        // Scaled path (World scale ≠ 1): the buckets are in unscaled voxels and the placement rounds each
        // voxel after scaling, so this path keeps the single Y-sorted walk.
        const f = stampVoxelsByY(stamp);
        const ys = f.ys, xs = f.xs, zs = f.zs, mats = f.mats, weights = f.weights;
        // Shrink/grow the voxel model about its base by `scale`. Voxel offsets are rounded to the grid, so
        // several source voxels may collapse onto one target when shrinking (last write / blend wins) — a
        // smaller model. ys is sorted, and round(y·s) is monotonic, so the Y-window bounds still work.
        let a = 0, b = f.n;
        while (a < b) {
            const m = (a + b) >> 1;
            if (Math.round(ys[m] * scale) < loY)
                a = m + 1;
            else
                b = m;
        }
        for (let i = a; i < f.n; i++) {
            const localY = Math.round(ys[i] * scale) - loY;
            if (localY >= CHUNK_SIZE)
                break;
            const localX = baseX + sx * Math.round(xs[i] * scale);
            const localZ = baseZ + sz * Math.round(zs[i] * scale);
            if (localX < 0 || localX >= CHUNK_SIZE || localZ < 0 || localZ >= CHUNK_SIZE)
                continue;
            const index = voxelIndex(localX, localY, localZ);
            const newVoxel = this.blendVoxel(data[index], mats[i], weights[i]);
            if (newVoxel !== null)
                data[index] = newVoxel;
        }
    }
    /**
     * Blend a stamp voxel with existing chunk voxel
     * @returns New packed voxel value, or null if no change
     */
    blendVoxel(existing, material, weight) {
        const existingWeight = getWeight(existing);
        // Air voxels (negative weight, material 0) always carve out space
        // This is used by buildings to create hollow interiors
        if (material === 0 && weight < 0) {
            // Only carve if existing is solid
            if (existingWeight > weight) {
                return packVoxel(weight, 0, 0);
            }
            return null;
        }
        // THE NEGATIVE HALF OF THE SURFACE BAND — air that is nearer the model than a saturated -0.5, and
        // the reason a stamp's crossing lands on its real surface instead of being pulled inward (see
        // voxel/surfaceBand.ts; a radius-8 sphere drew at 3.25 without this at lod 3).
        //
        // A MAX, and material NEVER moves. The max is what separates it from the carve above: it can only
        // raise a voxel toward the surface, so it cannot erode terrain, cannot cut a hole, and does not
        // care what order stamps are applied in. Leaving the material alone is what stops a ring of
        // recoloured air appearing around every tree.
        if (weight < 0) {
            return weight > existingWeight ? setWeight(existing, weight) : null;
        }
        switch (this.config.blendMode) {
            case BlendMode.MAX_WEIGHT:
                // Only apply if stamp voxel is more solid
                if (weight > existingWeight) {
                    return packVoxel(weight, material, 0);
                }
                return null;
            case BlendMode.REPLACE_SOLID:
                // Replace if stamp voxel is solid (weight > 0)
                if (weight > 0) {
                    return packVoxel(weight, material, 0);
                }
                return null;
            case BlendMode.ADDITIVE:
                // Add weights together, clamped
                const combinedWeight = Math.min(0.5, existingWeight + weight);
                // Use stamp material if it's making things more solid
                if (weight > 0) {
                    return packVoxel(combinedWeight, material, 0);
                }
                return null;
            default:
                return null;
        }
    }
}
//# sourceMappingURL=StampPlacer.js.map